import React, { useRef, useState, useCallback } from 'react'
import {
  Button,
  Dialog,
  Input,
  Switch,
  Select,
  Toast,
  Tooltip,
  Icon,
} from 'common/ui'
import { useDioxContext } from 'common/hooks'
import { dapp as getDAppService } from 'common/api'
import styled from 'styled-components'
import { useEffect } from 'react'
import { makeFinishUrl } from '../utils'
import { DIO_AMOUNT, DIO_AMOUNT_ERROR, getTipText } from '../constants'
import { withRouter, NextRouter, useRouter } from 'next/router'
import { DEFAULT, NETS, bignumberMult, getDefaultToken } from 'common/utils'

const dappService = getDAppService()
const CreateDAppStyled = styled.div`
  margin-top: 40px;
  width: 398px;
  .label {
    margin-bottom: 12px;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: #a3a3a3;
    &:not(:nth-child(1)) {
      margin-top: 24px;
    }
  }
  .supply {
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    display: flex;
    align-items: center;
    color: #ffffff;
    margin-top: 32px;
    justify-content: space-between;
  }
  .decimals {
    width: 160px;
  }
  .btn-group {
    display: flex;
    justify-content: end;
    align-items: center;
    margin-top: 58px;
    .cancel-btn {
      margin-right: 16px;
    }
  }
  .amount-tip {
    display: flex;
    align-item: center;
    svg {
      margin-left: 4px;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    .tooltip-container {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .amount-relative {
    position: relative;
    .amount-unit {
      position: absolute;
      right: 12px;
      top: 15px;
      font-weight: 400;
      font-size: 15px;
      line-height: 18px;
      color: #a3a3a3;
    }
  }
`
type CreateDAppContentProps = {
  onVerify: (result: boolean, txn?: string) => void
}
const NAME_PLACE = '4-8 alphanumeric digits and _'
const NAME_REPREAT = 'Name is currently occupied'
const DEPOSIT_REPREAT = 'Positive integer'
const NameReg = new RegExp(/^[a-zA-Z0-9_\-]{4,8}$/)

const CreateDAppContent = ({ onVerify }: CreateDAppContentProps) => {
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [amount, setAmount] = useState<string>()
  const [amountError, setAmountError] = useState<string>()
  const [disabled, setDisabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const { dioxide } = useDioxContext()
  const defaultToken = getDefaultToken()

  const verifyName = useCallback(async () => {
    if (name === undefined || !NameReg.test(name) || name.length > 16) {
      setNameError(NAME_PLACE)
      return false
    }
    const res = await dappService.getDAppInfo(name)
    if (res.Result?.ID) {
      setNameError(NAME_REPREAT)
      return false
    }
    setNameError('')
    return true
  }, [name])

  const verifyAmount = useCallback(() => {
    const min = defaultToken.SYMBOL === NETS.DIOXIDE ? 1 : 0.000001
    if (
      amount === undefined ||
      Number(amount) < 1 ||
      !DIO_AMOUNT.test(amount)
    ) {
      setAmountError(DIO_AMOUNT_ERROR())
      return false
    } else {
      setAmountError('')
      return true
    }
  }, [amount, onVerify])

  const handleClose = useCallback(() => {
    onVerify && onVerify(true)
  }, [onVerify])

  useEffect(() => {
    if (!nameError && name && !amountError && amount) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [nameError, name, amount, amountError])

  const handleConfirm = useCallback(async () => {
    const nameResult = await verifyName()
    const amountResult = await verifyAmount()
    if (!nameResult || !amountResult) return false
    setLoading(true)
    try {
      const txn = await dioxide.request({
        method: 'send_transaction',
        params: {
          func: 'core.delegation.create',
          args: {
            Name: name,
            Type: 10,
            Deposit: bignumberMult(amount || 0, 10 ** defaultToken.DECIMAL),
          },
        },
      })
      onVerify && onVerify(true, txn)
    } catch (e: any) {
      Toast({ text: e?.message as string })
    }
    setLoading(false)
  }, [dioxide, onVerify, verifyName, verifyAmount, amount, name])

  return (
    <CreateDAppStyled>
      <div className="label">Set the name of the DApp</div>
      <Input
        value={name}
        onChange={setName}
        onBlur={verifyName}
        countdown={8}
        placeholder={NAME_PLACE}
        error={nameError}
      />
      <div className="label amount-tip">
        Creation Deposit
        <Tooltip
          placement="topLeft"
          overlayStyle={{ width: '386px', minHeight: '168px' }}
          overlay={getTipText('DApp')}
        >
          <Icon type="tip" />
        </Tooltip>
      </div>
      <div className="amount-relative">
        <Input
          className="amount-input"
          type="number"
          maxLength={18}
          maxValue={184467440737}
          value={amount}
          onChange={setAmount}
          onBlur={verifyAmount}
          error={amountError}
          placeholder={DEPOSIT_REPREAT}
        />
        <span className="amount-unit">{defaultToken.SYMBOL}</span>
      </div>
      <div className="btn-group">
        <Button
          loading={loading}
          className="cancel-btn"
          type="outline"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={disabled}
          type="primary"
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </div>
    </CreateDAppStyled>
  )
}
type CreateDAppBtnProps = {
  children: JSX.Element | string
  router: NextRouter
  icon?: string
}
const CreateDAppBtn = ({ children, router, icon }: CreateDAppBtnProps) => {
  const creatDAppRef = useRef<{ close: () => void }>()
  const { asPath, pathname, push } = useRouter()

  useEffect(() => {
    if (asPath && asPath.match('action=')) {
      const action = asPath.match(/action=(\S+)/)
      if (action && action[1]) {
        const Event = action[1]
        if (Event === 'create') {
          handleCreatDApp()
        }
        setTimeout(() => {
          push(pathname)
        }, 1000)
      }
    }
  }, [asPath])

  const handleVerify = useCallback(
    (result, txn) => {
      if (result) {
        creatDAppRef.current?.close && creatDAppRef.current?.close()
        txn && router.push(makeFinishUrl(txn, { target: 'createDApp' }))
      }
    },
    [creatDAppRef]
  )

  const handleCreatDApp = () => {
    Dialog({
      title: 'Create DApp',
      content: <CreateDAppContent onVerify={handleVerify} />,
      ref: creatDAppRef,
    })
  }

  useEffect(() => {
    return () => {
      creatDAppRef.current?.close && creatDAppRef.current?.close()
    }
  }, [creatDAppRef])

  return (
    <Button icon={icon} onClick={handleCreatDApp} type="primary" size="large">
      {children}
    </Button>
  )
}

export default withRouter(CreateDAppBtn)
